import { Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  ControlContainer,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidationErrors,
} from '@angular/forms';
import * as _ from 'lodash';
import { Observable, map, startWith, tap, take } from 'rxjs';

interface ObjectMapper {
  [key: string]: any;
}
@Component({
    selector: 'pr-input-autosuggest',
    templateUrl: './pr-input-autosuggest.component.html',
    styleUrls: ['./pr-input-autosuggest.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PrInputAutosuggestComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: PrInputAutosuggestComponent,
        },
    ],
    standalone: false
})
export class PrInputAutosuggestComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @ViewChild('input', { static: true }) input: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  @Input() formControlName: string;
  @Input() derrors = false;
  @Input() loading = false;
  @Input() highlightOnValue = false;
  @Input() label: string;
  @Input() placeholder = '';
  @Input() optionLabel: string;
  @Input() additionalOptionLabel: {
    name: string;
    useParentheses: boolean;
    useDash: boolean;
  }[] = [];
  @Input() objectMapper: ObjectMapper = null;
  @Input() optionValue = 'id';
  @Input() noCustom = false;
  @Input() onCreateOption: (label: string) => Observable<any>;
  @Input() isDefault = true;
  @Input() iconSide: 'left' | 'right' = 'right';
  @Input() favorites: any[];
  @Input() optionIcon: (string) => string[];
  $filteredOptions: Observable<any[]>;
  filteredOptionsFavorite: any[] = [];
  filteredOptions: any[];
  value: any;
  _ = _;
  touched = false;
  disabled = false;
  creating = false;

  constructor(
    private controlContainer: ControlContainer,
    private ngZone: NgZone,
  ) {}

  _options = [];

  get options() {
    return this._options;
  }

  @Input() set options(options) {
    if (!options) {
      return;
    }
    this._options = options;
    this.setupOptionFilterLogic();
  }

  get control(): UntypedFormControl {
    return this.controlContainer.control.get(
      this.formControlName,
    ) as UntypedFormControl;
  }

  get showAddButton() {
    return (
      !this.noCustom &&
      this.filteredOptions &&
      this.input.nativeElement.value &&
      !_.map(this.filteredOptions, o =>
        this.optionLabel ? o[this.optionLabel].toLowerCase() : o.toLowerCase(),
      ).includes(this.input.nativeElement.value.toLowerCase())
    );
  }

  onChange = () => {};

  onTouched = () => {};

  private scrollListener = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target?.classList?.contains('mat-mdc-autocomplete-panel')) return;
    this.autocompleteTrigger?.closePanel();
  };

  ngOnInit(): void {
    this.control.setValue(this.control.value);
    if (!this.optionIcon) {
      this.optionIcon = () => undefined;
    }
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scrollListener, { capture: true });
  }

  displayFn(option) {
    if (typeof option === 'string') {
      return option;
    } else if (option && this.optionLabel) {
      return option[this.optionLabel];
    } else {
      return undefined;
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  // Interface

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  getAdditionalOptionLabel(option: any, label: string): string {
    if (this.objectMapper && option?.[label] in this.objectMapper) {
      return this.objectMapper[option[label]];
    } else {
      return option[label];
    }
  }

  validate(): ValidationErrors | null {
    // TODO:must refactor this validation
    // temprarily fix this line
    if (this.control && !this.control?.invalid) {
      return null;
    }
    if (this.noCustom && !this._options.includes(this.control.value)) {
      return { wrong: { value: true } };
    }
    return null;
  }

  handleCreate(event: Event) {
    event.stopPropagation();
    if (!this.onCreateOption || this.creating) return;

    const inputValue = this.input.nativeElement.value?.trim();
    if (!inputValue) return;

    this.creating = true;
    this.onCreateOption(inputValue).pipe(take(1)).subscribe({
      next: (created) => {
        if (created) {
          this._options = [...this._options, created];
          this.control.setValue(created);
          this.autocompleteTrigger?.closePanel();
        }
        this.creating = false;
      },
      error: () => {
        this.creating = false;
      },
    });
  }

  private _filter(value: any): string[] {
    const filterValue = value ? value.toLowerCase() : '';
    return this._options
      ? this._options.filter(option =>
          option[this.optionLabel]?.toLowerCase()?.includes(filterValue),
        )
      : [];
  }

  private tryToFindCorrespondingOption() {
    let correspondingOption;

    if (typeof this.control.value === 'string') {
      correspondingOption = this.options.find(option =>
        this.optionLabel
          ? option[this.optionLabel] === this.control.value
          : option === this.control.value,
      );
    }

    if (correspondingOption) {
      this.control.setValue(correspondingOption, { emitEvent: false });
      this.input.nativeElement.value = this.optionLabel
        ? correspondingOption[this.optionLabel]
        : correspondingOption;
    } else if (
      !this.noCustom &&
      !correspondingOption &&
      typeof this.control.value === 'string'
    ) {
      let obj = {};
      obj[this.optionLabel] = this.control.value;
      this.control.setValue(obj, { emitEvent: false });
    }
  }

  private setupOptionFilterLogic() {
    this.$filteredOptions = this.control.valueChanges.pipe(
      startWith(''),
      map(value =>
        typeof value === 'string' || !value ? value : value[this.optionLabel],
      ),
      map(name => (name ? this._filter(name) : this.options.slice())),
      tap(
        options =>
          (this.filteredOptionsFavorite = _(options)
            .filter(
              opt =>
                this.favorites?.length > 0 &&
                this.favorites.includes(opt[this.optionValue].toLowerCase()),
            )
            .value()),
      ),
      map(options =>
        _(options)
          .filter(
            opt =>
              !this.favorites?.length ||
              !this.favorites?.includes(opt[this.optionValue].toLowerCase()),
          )
          .value(),
      ),
      tap(value => (this.filteredOptions = value)),
      tap(() => {
        if (this.options.length > 0) {
          this.tryToFindCorrespondingOption();
        }
      }),
    );
  }
}
